import { Container, Graphics, Rectangle, Sprite, Text } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';
/** Button radius — gives a 80px diameter circle. Sized so the
 * INFO + SETTINGS pair on the slot HUD reads as confidently tappable
 * (the prior 64px was reported as "too small" in playtest). Still
 * smaller than SPIN's 192px disc, which stays the visual focal point.
 *
 * <p>Producer audit Phase 4 (round 3) — bumped 32 → 40 so the icons
 * read at a confident touch-target size. The icon sprite size scales
 * proportionally below. */
const BUTTON_RADIUS = 40;
/** Pixel size the sprite renders at when a textureAlias is provided.
 * Sized to ~65% of the button's diameter so the icon reads cleanly
 * without crowding the circle's edge. */
const SPRITE_SIZE = 52;

export interface IconButtonOptions {
  /** Asset alias for a Pixi Sprite to render in place of the text
   * glyph. When provided AND the texture has loaded through
   * {@code AssetLoader.shared}, a Sprite renders centred in the
   * button instead of the glyph. Falls back to the glyph when the
   * texture is unavailable so engine-only / unstyled scenes still
   * read. */
  textureAlias?: string;
  /** Stroke colour for the button's circular border. Defaults to the
   * brand pink. */
  accent?: number;
  /** Optional commissioned full-button image set. When the idle
   * texture resolves, the widget skips the drawn circle background
   * and renders the commissioned art as the entire button — texture
   * swaps on pointer-down. Pressed falls back to idle when missing. */
  imageIdleAlias?: string;
  imagePressedAlias?: string;
  /** Override the button radius (default {@link BUTTON_RADIUS}). Sets
   *  both the drawn-circle size and the hit-area. Useful when the host
   *  renders multiple smaller icons inside a shared background pill
   *  (e.g. the slot HUD's INFO+SETTINGS cluster). */
  radius?: number;
  /** When true, skip drawing the circle background + border + drop
   *  shadow. The glyph + an explicit hit-area Rectangle still render.
   *  Used when the host wraps multiple icons in a single shared pill
   *  so the per-icon circles don't fight the shared background. */
  transparent?: boolean;
  /** Optional custom-draw hook. When provided, replaces the
   *  glyph-text rendering with a Pixi-drawn vector shape — used for
   *  consistent icon sizing across symbols that render at different
   *  em-box scales in the platform font (e.g. {@code ⚙} renders
   *  noticeably smaller than {@code ⓘ} at the same fontSize). The
   *  callback receives the container to add Graphics to + the target
   *  size (typically the button's diameter × 0.7 for breathing room).
   *  The container's origin is at the button centre. */
  customDraw?: (container: Container, size: number) => void;
}

/**
 * Small circular button with a single icon centred in it. Originally
 * used for the paytable info button (ⓘ) — now reusable for any
 * glyph-only action, with optional swap to a commissioned PNG via
 * {@link IconButtonOptions#textureAlias}.
 *
 * <p>Accent colour drives the stroke; the glyph (when used) is white.
 * The sprite (when used) renders at native colours.
 */
export class IconButton extends Container {
  constructor(glyph: string, onTap: () => void, options: IconButtonOptions | number = {}) {
    super();
    this.eventMode = 'static';
    this.cursor = 'pointer';

    // Backward-compat: the original signature took the accent colour
    // as the third positional argument. Detect a number and treat it
    // as { accent: <number> } so callers stay source-compatible.
    const opts: IconButtonOptions =
      typeof options === 'number' ? { accent: options } : options;
    const accent = opts.accent ?? 0xff6bd6;
    const radius = opts.radius ?? BUTTON_RADIUS;
    // Sprite + glyph sizes scale with the (possibly overridden) radius
    // so a smaller IconButton stays proportional.
    const spriteSize = (SPRITE_SIZE / BUTTON_RADIUS) * radius;
    const glyphFontSize = (36 / BUTTON_RADIUS) * radius;

    // Image-mode short-circuit: full commissioned button texture.
    const idleTex = opts.imageIdleAlias
      ? AssetLoader.shared.getTexture(opts.imageIdleAlias)
      : null;
    const pressedTex = opts.imagePressedAlias
      ? AssetLoader.shared.getTexture(opts.imagePressedAlias)
      : null;
    if (idleTex) {
      const sprite = new Sprite(idleTex);
      sprite.anchor.set(0.5);
      const aspect = idleTex.width / idleTex.height;
      sprite.height = radius * 2;
      sprite.width = radius * 2 * aspect;
      this.addChild(sprite);
      this.on('pointerdown', () => {
        if (pressedTex) sprite.texture = pressedTex;
      });
      this.on('pointerup', () => {
        sprite.texture = idleTex;
      });
      this.on('pointerupoutside', () => {
        sprite.texture = idleTex;
      });
      this.on('pointertap', onTap);
      return;
    }

    // Drawn-circle background — mirrors the BET pill's navy + gold-trim
    // visual language (per the slot-HUD redesign brief). Skipped when
    // {@link IconButtonOptions#transparent} is true — the host renders
    // its own shared background (e.g. the slot HUD's INFO+SETTINGS
    // cluster pill) and the per-icon circles would fight it. When
    // transparent, an explicit hit-area Rectangle preserves the tap
    // target so the icon is still confidently tappable.
    if (!opts.transparent) {
      const bg = new Graphics();
      bg
        // Drop-shadow ground — slightly larger circle offset down/right.
        .circle(1, 3, radius)
        .fill({ color: 0x000000, alpha: 0.45 })
        // Navy body.
        .circle(0, 0, radius)
        .fill({ color: 0x16113d, alpha: 1 })
        // Top highlight — slightly brighter half-disc near the top,
        // sells the lit-surface read.
        .circle(0, -radius / 4, radius - 4)
        .fill({ color: 0x2a1e63, alpha: 0.55 })
        // Outer accent stroke (gold by default — see accent override).
        .circle(0, 0, radius)
        .stroke({ color: accent, alpha: 1, width: 3 })
        // Inner soft-accent glow line — second stroke inside the border.
        .circle(0, 0, radius - 3)
        .stroke({ color: accent, alpha: 0.35, width: 1 });
      this.addChild(bg);
    } else {
      // Transparent mode — explicit hit-area Rectangle so the tap
      // target persists even though no visible circle bg is drawn.
      this.hitArea = new Rectangle(-radius, -radius, radius * 2, radius * 2);
    }

    // Icon rendering precedence:
    //   1. customDraw — vector-drawn shape; used when the host wants
    //      pixel-perfect consistent sizing across icons (e.g. info +
    //      gear inside the slot HUD cluster).
    //   2. textureAlias — Pixi Sprite from the asset bundle.
    //   3. glyph Text — Unicode character (fallback for engine-only
    //      test scenes; rendering varies across platform fonts).
    const texture = opts.textureAlias
      ? AssetLoader.shared.getTexture(opts.textureAlias)
      : null;
    if (opts.customDraw) {
      const drawContainer = new Container();
      // Target size is 70% of the button diameter so the drawn icon
      // has visible breathing room from the (potentially invisible)
      // disc edge.
      opts.customDraw(drawContainer, radius * 2 * 0.7);
      this.addChild(drawContainer);
    } else if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      const aspect = texture.width / texture.height;
      sprite.height = spriteSize;
      sprite.width = spriteSize * aspect;
      this.addChild(sprite);
    } else {
      const text = new Text({
        text: glyph,
        style: {
          fontFamily: FONT_FAMILY,
          // Glyph scales with the button radius — a 40-radius button
          // gets a 36px glyph; smaller buttons get proportionally
          // smaller glyphs.
          fontSize: glyphFontSize,
          fontWeight: '900',
          fill: 0xffffff,
        },
      });
      text.anchor.set(0.5);
      this.addChild(text);
    }

    this.on('pointertap', onTap);
  }
}
